import { computed, inject, Injectable, signal } from '@angular/core';
import { DataReaderService } from '@core/lp/data-reader.service';
import { MutationTreeBuilder } from '@features/wk8/mechanics/mutation-path/utils/mutation.utils';
import { WordToKlankConverter } from '@shared/woordkr8/WordToKlankConverter';
import { LpWoordData } from '@typings/lp/generated';
import _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
/**
 * Finds words similar to the given word based on the difference in their phoneme (klank) representations.
 *
 * This method compares the klank breakdown of the input word to other words in the list,
 * returning those whose klank sequences differ by at most `maxKlankDifference`.
 * The comparison can be performed on either all words or only the filtered set, as specified by `list`.
 *
 * @param word - The reference word to find similar words for.
 * @param maxKlankDifference - The maximum number of klank (phoneme) differences allowed.
 * @param list - Determines which word list to use: `'ALL'` for all words, `'FILTERED'` for valid/filtered words. Defaults to `'FILTERED'`.
 * @returns An array of words similar to the input word, based on klank difference.
 */
export class Wk8WordService {
  readonly #dataReaderService = inject(DataReaderService);
  private readonly allWords = signal<LpWoordData[]>([]);
  private readonly validWords = computed(() => this.allWords().filter((word) => word.image !== undefined && !word.hide));
  private readonly allWordsToKlanken = computed(() => {
    const map: Record<string, string[]> = {};
    this.allWords().forEach((word) => {
      map[word.word] = WordToKlankConverter.breakupWord(word.word);
    });
    return map;
  });

  public readonly words = computed(() => this.validWords());

  private isLoaded = false;

  constructor() {
    this.waitForLoad();
  }

  public async waitForLoad() {
    if (this.isLoaded) return;

    const words = await this.#dataReaderService.getInstancesByType('WoordData');
    this.allWords.set(words);
    this.isLoaded = true;
  }

  public tryFindWord(word: string, list: 'ALL' | 'FILTERED' = 'FILTERED'): LpWoordData | undefined {
    return (list === 'FILTERED' ? this.words() : this.allWords()).find((w) => w.word === word);
  }

  public findWord(word: string, list: 'ALL' | 'FILTERED' = 'FILTERED'): LpWoordData {
    const foundWord = this.tryFindWord(word, list);
    if (!foundWord) {
      throw new Error(`Word '${word}' not found in the list of valid words`);
    }

    return foundWord;
  }

  public exists(word: string, list: 'ALL' | 'FILTERED' = 'FILTERED'): boolean {
    return this.tryFindWord(word, list) !== undefined;
  }

  public generateIncorrectWords(correctWord: string): string[] {
    const correctKlanken = this.allWordsToKlanken()[correctWord];

    return correctKlanken.map((targetKlank, index) => {
      const candidateWords = this.words().filter(({ word }) => {
        if (word === correctWord) return false;
        const wordKlanken = this.allWordsToKlanken()[word];
        return wordKlanken.length === correctKlanken.length && wordKlanken[index] === targetKlank;
      });

      if (candidateWords.length > 0) {
        return _.sample(candidateWords)!.word;
      }

      // Generate a word if no candidates found
      const generatedKlanken = correctKlanken.map((klank, i) => {
        if (i === index) {
          return klank;
        } else if (WordToKlankConverter.isKlinker(klank)) {
          return _.sample(WordToKlankConverter.getKlinkers()) ?? klank;
        } else if (WordToKlankConverter.isMedeklinker(klank)) {
          return _.sample(WordToKlankConverter.getMedeklinkers()) ?? klank;
        }
        return klank;
      });

      return generatedKlanken.join('');
    });
  }

  public generateRandomMutationPath(word: string, maxLength: number, list: 'ALL' | 'FILTERED' = 'FILTERED') {
    const mutationTreeBuilder = new MutationTreeBuilder(this.words().map((w) => w.word));

    return mutationTreeBuilder
      .getRandomMutationPath(word, maxLength) //
      .map((w) => this.findWord(w, list));
  }

  /*
   * Find words that differ at most a given number of klanken, excluding the word itself.
   * @param word - The reference word to find similar words for.
   * @param maxKlankDifference - The maximum number of klank differences allowed.
   * @param list - Determines which word list to use: 'ALL' for all words, 'FILTERED' for valid/filtered words. Defaults to 'FILTERED'.
   * @returns An array of words similar to the input word, excluding the input word.
   */
  public findSimilarWords(word: string, maxKlankDifference: number, list: 'ALL' | 'FILTERED' = 'FILTERED') {
    const wordList = list === 'FILTERED' ? this.words() : this.allWords();
    return Wk8WordService.findSimilarWordsPure(
      word,
      maxKlankDifference,
      wordList.map((w) => w.word).filter((w) => w !== word),
      this.allWordsToKlanken(),
    );
  }

  public static findSimilarWordsPure(
    word: string,
    maxKlankDifference: number,
    wordList: string[],
    wordToKlanken?: Record<string, string[]>,
  ): string[] {
    const safeWordToKlanken = wordToKlanken ?? {};
    const targetKlanken = safeWordToKlanken[word] ?? WordToKlankConverter.breakupWord(word);
    const targetLength = targetKlanken.length;
    const similarWords: string[] = [];
    for (const candidate of wordList) {
      const candidateKlanken = safeWordToKlanken[candidate] ?? WordToKlankConverter.breakupWord(candidate);
      const candidateLength = candidateKlanken.length;
      if (Math.abs(candidateLength - targetLength) > maxKlankDifference) {
        continue;
      }
      let differences = 0;
      for (let i = 0; i < Math.max(targetLength, candidateLength); i++) {
        const targetKlank = targetKlanken[i] ?? '';
        const candidateKlank = candidateKlanken[i] ?? '';
        if (targetKlank !== candidateKlank) {
          differences++;
          if (differences > maxKlankDifference) {
            break;
          }
        }
      }
      if (differences <= maxKlankDifference) {
        similarWords.push(candidate);
      }
    }
    return similarWords;
  }

  public getNumWordsAdaptively(number: number, list: 'ALL' | 'FILTERED' = 'FILTERED', excludeUids: string[] = []): LpWoordData[] {
    const words = list === 'FILTERED' ? this.words() : this.allWords();
    const filteredWords = words.filter((word) => !excludeUids.includes(word.uid));

    if (filteredWords.length < number) {
      throw new Error(`Not enough words available. Requested: ${number}, Available: ${words.length}`);
    }

    // Shuffle the words and take the first 'number' elements
    return _.sampleSize(words, number);
  }
}
